import { supabase } from '../lib/supabase';
import { Capacitor } from '@capacitor/core';
import { v4 as uuidv4 } from 'uuid';
import { MembershipType } from '../types/saas';
import { joinTeamWithCode } from './team.service';
import { createTrialSubscription } from './subscriptionService';

export const generateDeviceId = (): string => uuidv4();

// Telefon numarasını standart formata dönüştür: sadece rakamlar, başındaki 0 veya +90 kaldır
export const normalizePhone = (phone: string): string => {
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('90') && digits.length === 12) digits = digits.slice(2);
  if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
  return digits; // örn: "5321234567"
};

const EMAIL_DOMAIN = process.env.REACT_APP_EMAIL_DOMAIN || 'atlasdefteri.com';

const phoneToEmail = (phoneNumber: string): string => {
  return normalizePhone(phoneNumber) + '@' + EMAIL_DOMAIN;
};

export const generateDesktopCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: 8 }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join('');
};

export const registerUser = async (request: any): Promise<any> => {
  const { phoneNumber, email: reqEmail, name, firstName, lastName, password, membershipType, desktopCode, teamCode } = request;

  if (!phoneNumber) throw new Error('Geçerli bir telefon numarası giriniz.');

  const email = phoneToEmail(phoneNumber);
  const finalPassword = password || 'Atlas2024!';

  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: email,
    password: finalPassword
  });

  if (authError) {
    throw new Error(authError.message || 'Kayıt olurken bir hata oluştu');
  }

  const userId = authData.user?.id;
  if (!userId) throw new Error('Auth ID alınamadı');

  const finalDesktopCode = membershipType === MembershipType.DESKTOP && !desktopCode
    ? generateDesktopCode()
    : desktopCode;

  // invite_code oluştur
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const inviteCode = Array.from({ length: 6 }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');

  if (teamCode) {
    const { error: profileError } = await supabase
      .from('user_profiles')
      .insert([{
        id: userId,
        phone_number: phoneNumber,
        email: reqEmail || null,
        first_name: name?.split(' ')[0] || firstName || 'Yeni',
        last_name: name?.split(' ').slice(1).join(' ') || lastName || 'Kullanıcı',
        role: 'user',
        desktop_code: finalDesktopCode || null,
        is_active: true
      }]);

    if (profileError) throw profileError;
    const joinedTeam = await joinTeamWithCode(teamCode, userId);
    await createTrialSubscription(userId, joinedTeam?.id);
  } else {
    const { data: newTeam, error: teamError } = await supabase
      .from('teams')
      .insert({
        name: 'Yeni Takım',
        status: 'active',
        invite_code: inviteCode,
        owner_id: userId
      })
      .select()
      .single();

    if (teamError) throw new Error('Takım oluşturulamadı: ' + teamError.message);

    const { error: profileError } = await supabase
      .from('user_profiles')
      .insert([{
        id: userId,
        phone_number: phoneNumber,
        email: reqEmail || null,
        first_name: name?.split(' ')[0] || firstName || 'Yeni',
        last_name: name?.split(' ').slice(1).join(' ') || lastName || 'Kullanıcı',
        role: 'super_admin',
        team_id: newTeam.id,
        team_role: 'OWNER',
        desktop_code: finalDesktopCode || null,
        is_active: true
      }]);

    if (profileError) throw profileError;
    await createTrialSubscription(userId, newTeam.id);
  }

  return {
    user: authData.user,
    session: authData.session
  };
};

export const loginUser = async (request: any): Promise<any> => {
  const { phoneNumber, password: userPassword } = request;

  if (!phoneNumber) throw new Error('Telefon numarası gereklidir.');

  const email = phoneToEmail(phoneNumber);
  const password = userPassword || 'Atlas2024!';

  const { data, error } = await supabase.auth.signInWithPassword({
    email: email,
    password: password
  });

  if (error) {
    if (error.message.includes('Invalid login credentials')) {
      throw new Error('Kullanıcı bulunamadı veya bilgiler hatalı.');
    }
    throw new Error(error.message);
  }

  return {
    user: data.user,
    session: data.session
  };
};

const getRedirectUrl = (): string => {
  if (Capacitor.isNativePlatform()) {
    return 'com.atlaslojistik.sevkiyatdefterim://login-callback';
  }
  return window.location.origin;
};

// Google ile giriş
export const loginWithGoogle = async (): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
    try {
      await GoogleAuth.initialize({
        clientId: '922069554523-cf2uleeoqq89u4hnv92ahgp2vhm0pokj.apps.googleusercontent.com',
        scopes: ['profile', 'email'],
        grantOfflineAccess: true,
      });
      const result = await GoogleAuth.signIn();
      const idToken = result?.authentication?.idToken;
      if (!idToken) throw new Error('Google ID token alınamadı');
      const { error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: idToken,
      });
      if (error) throw new Error(error.message || 'Google ile giriş yapılamadı');
    } catch (err: any) {
      // Kullanıcı iptal etti
      const code = err?.code ?? err?.errorCode;
      const msg: string = err?.message ?? '';
      if (code === '12501' || code === 12501 || msg.toLowerCase().includes('cancel') || msg.includes('12501')) {
        return;
      }
      throw err;
    }
  } else {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: getRedirectUrl() }
    });
    if (error) throw new Error(error.message || 'Google ile giriş yapılamadı');
  }
};

// Apple ile giriş
export const loginWithApple = async (): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
    try {
      const result = await SignInWithApple.authorize({
        clientId: 'com.atlaslojistik.sevkiyatdefterim',
        redirectURI: 'https://uqmvjagdjywvuknsepbi.supabase.co/auth/v1/callback',
        scopes: 'email name',
      });
      const identityToken = result.response?.identityToken;
      if (!identityToken) throw new Error('Apple kimlik token alınamadı');
      const { error } = await supabase.auth.signInWithIdToken({
        provider: 'apple',
        token: identityToken,
      });
      if (error) throw new Error(error.message || 'Apple ile giriş yapılamadı');
    } catch (err: any) {
      // Kullanıcı iptal etti → sessizce çık
      const code = err?.code ?? err?.errorCode;
      const msg: string = err?.message ?? '';
      if (code === '1001' || code === 1001 || msg.includes('1001') || msg.toLowerCase().includes('cancel')) {
        return;
      }
      throw err;
    }
  } else {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo: getRedirectUrl() }
    });
    if (error) throw new Error(error.message || 'Apple ile giriş yapılamadı');
  }
};

// Hesap sil (Apple zorunluluğu)
export const deleteAccount = async (): Promise<void> => {
  const { error } = await supabase.rpc('delete_my_account');
  if (error) throw new Error(error.message || 'Hesap silinemedi');
  await supabase.auth.signOut();
};

// Şifre sıfırlama
export const resetPassword = async (email: string): Promise<void> => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${getRedirectUrl()}/reset-password`
  });
  if (error) throw new Error(error.message || 'Şifre sıfırlama e-postası gönderilemedi');
};

export const logoutUser = async (): Promise<void> => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

export const validateSession = async (): Promise<any> => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error || !session) return null;
  return session;
};

export const onAuthStateChanged = (callback: (session: any) => void) => {
  const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session);
  });
  return authListener.subscription;
};
